import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { coursesReportsSchema, defaultValues } from '../schemas/courses-reports-schema';
import type { CourseReportFilterI } from 'types';
import usePrioritiesApi from 'Hooks/usePrioritiesApi';
import { useEffect } from 'react';
import useDebounce from 'Hooks/useDebounce';
import useTranslation from 'Hooks/useTranslation';

interface Props {
  onSearch: (filters: CourseReportFilterI) => void;
}

const useCoursesReportFilters = ({ onSearch }: Props) => {
  /**
   * Hook de react-hook-form para manejar el formulario de filtros.
   */
  const { t2 } = useTranslation('courses_reports');

  const {
    control,
    register,
    formState: { errors, isDirty },
    reset,
    watch,
    getValues,
  } = useForm<CourseReportFilterI>({
    defaultValues: defaultValues,
    resolver: zodResolver(coursesReportsSchema),
  });

  /**
   * Un custom hook que se encarga de obtener todas las prioridades de los cursos
   * que estan disponibles en el sistema.
   */
  const {
    data: prioritiesData,
    loading: prioritiesLoading,
    error: prioritiesError,
  } = usePrioritiesApi({
    init: true,
  });

  /**
   * Resetea los filtros y vuelve a realizar una consulta para obtener los datos
   * sin los filtros aplicados.
   */
  const onResetFilters = () => {
    reset(defaultValues);
    onSearch({});
  };

  /**
   * Se escuchan los cambios del nombre del curso y se realiza una consulta
   * automática sin la necesidad de presionar el botón de búsqueda.
   */
  const nameValue = watch('name');
  const debouncedName = useDebounce(nameValue, 500);

  useEffect(() => {
    if (debouncedName !== undefined) {
      const values = getValues();
      onSearch(values);
    }
  }, [debouncedName]);

  return {
    control,
    register,
    errors,
    prioritiesData,
    prioritiesLoading,
    prioritiesError,
    onResetFilters,
    isDirty,
    getValues,
  };
};

export default useCoursesReportFilters;
